import { createFileRoute, Link } from "@tanstack/react-router";
import {
  GuideArticleSection,
  GuideKeyTakeaways,
  GuideLayout,
} from "@/components/guides/GuideLayout";
import { BarcodeFigure } from "@/components/guides/BarcodeFigure";
import { Callout } from "@/components/guides/Callout";
import { Compare } from "@/components/guides/Compare";
import { TryTool } from "@/components/guides/TryTool";
import { TableScroll } from "@/components/layout/ContentBlock";
import { FaqSection } from "@/components/seo/FaqSection";
import { GUIDES_BY_SLUG } from "@/lib/guides";
import { pageHead, breadcrumbJsonLd, ldScript, absoluteUrl } from "@/lib/seo";

const GUIDE = GUIDES_BY_SLUG["sunrise-2027-explained"];

const TOC = [
  { id: "the-short-answer", label: "The short answer" },
  { id: "before-after", label: "Before vs after Sunrise 2027" },
  { id: "what-is-it", label: "What is GS1 Sunrise 2027?" },
  { id: "why-now", label: "Why now? The data-carrier limits of 1D" },
  { id: "at-the-till", label: "What changes at the till" },
  { id: "for-brands", label: "What brands need to do" },
  { id: "for-retailers", label: "What retailers need to do" },
  { id: "dual-carrier", label: "The dual-carrier interim" },
  { id: "misconceptions", label: "Common misconceptions" },
  { id: "quick-decision", label: "Quick decision guide" },
  { id: "requirements", label: "GS1 Sunrise 2027 requirements" },
  { id: "qr-codes-digital-link", label: "Sunrise 2027 QR codes" },
  { id: "references", label: "References" },
];

const FAQ = [
  {
    q: "Is Sunrise 2027 a Regulation or a Mandate?",
    a: "Neither, strictly. Sunrise 2027 is a commitment by GS1 and its member retailers and brands to have global retail POS accept 2D barcodes (GS1 Digital Link in QR, or GS1 Data Matrix) alongside traditional 1D EAN / UPC by end of 2027. Some markets and regulators are coupling it with their own rules — pharma traceability, deposit-return schemes — but the Sunrise commitment itself is industry-led.",
  },
  {
    q: "Will My Old 1D EAN / UPC Barcode Stop Working in 2027?",
    a: "No. The 1D barcode stays valid past 2027 — that's the whole point of running dual carriers (1D + 2D) on the same pack through the transition. POS scanners will keep reading the 1D EAN; 2D acceptance is being added on top, not as a replacement.",
  },
  {
    q: "Do I Need a GS1 Digital Link to Be Sunrise-Ready?",
    a: "Not strictly, but it's the route GS1 itself recommends and the one most brands are taking. The alternative is a plain GS1 Data Matrix carrying GTIN + AIs — same data, harder for shoppers to use because a Data Matrix doesn't open as a URL when scanned with a phone camera.",
  },
  {
    q: "What Does Sunrise 2027 Cost a Brand?",
    a: "If your packaging artwork is already on a refresh cycle, the marginal cost is small — add a QR Digital Link next to the existing EAN-13. The real cost shows up if you're starting from scratch on master data: you need a GTIN registered to your company prefix, an AI-aware data model (batch, expiry, serial), and a resolver if you want a branded URL rather than id.gs1.org.",
  },
  {
    q: "What Happens If My POS Scanner Can Only Read 1D?",
    a: "It'll keep working until you replace it — the 1D EAN on the pack is still there. The pressure to upgrade comes from your business case (richer scan data, fewer manual look-ups), not from a Sunrise deadline. Most modern imager scanners already handle 2D; older laser-line scanners don't, and those are the ones that need swapping.",
  },
  {
    q: "Is Sunrise 2027 the Same as the EU Pharma Serialisation Rules?",
    a: "No. EU FMD (Falsified Medicines Directive) and similar regional pharma traceability rules already require 2D GS1 Data Matrix on prescription packs and have done since 2019. Sunrise 2027 is the broader retail commitment for non-pharma consumer goods — your shampoo bottle, not your blister pack.",
  },
  {
    q: "Is Sunrise 2027 Mandatory?",
    a: "Not as a matter of law. Sunrise 2027 is a GS1 industry goal with no penalty attached, so nothing happens to a brand that misses the date. It can still become a condition of supply when a retailer writes 2D barcode rules into its supplier terms, so check the supplier manual of every retailer you sell through for its own date and accepted symbols.",
  },
];

export const Route = createFileRoute("/barcode-guides/sunrise-2027-explained")({
  component: Sunrise2027Guide,
  head: () => {
    const base = pageHead({
      title: GUIDE.title,
      description: GUIDE.description,
      path: GUIDE.path,
      ogType: "article",
    });
    return {
      ...base,
      scripts: [
        ldScript({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: GUIDE.title,
          description: GUIDE.description,
          mainEntityOfPage: absoluteUrl(GUIDE.path),
          author: { "@type": "Organization", name: "ReadBarcode" },
          publisher: { "@type": "Organization", name: "ReadBarcode" },
          datePublished: GUIDE.lastUpdated,
          dateModified: GUIDE.lastUpdated,
          about: GUIDE.keyword,
        }),
        ldScript(
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Barcode Guides", path: "/barcode-guides" },
            { name: GUIDE.shortTitle, path: GUIDE.path },
          ]),
        ),
      ],
    };
  },
});

function Sunrise2027Guide() {
  return (
    <GuideLayout
      guide={GUIDE}
      toc={TOC}
      nextRead={[
        {
          to: "/barcode-guides/gs1-digital-link",
          label: "GS1 Digital Link explained",
          desc: "The URL syntax that powers a Sunrise-ready consumer pack.",
        },
        {
          to: "/barcode-guides/1d-vs-2d-barcodes",
          label: "1D vs 2D barcodes",
          desc: "The capacity and scanner story behind the transition.",
        },
        {
          to: "/gs1-digital-link-generator",
          label: "GS1 Digital Link generator",
          desc: "Build a Sunrise 2027 QR code or Data Matrix from a GTIN.",
        },
        {
          to: "/gs1-digital-link-decoder",
          label: "GS1 Digital Link decoder",
          desc: "Check that a printed URL returns the right GTIN and AIs.",
        },
      ]}
    >
      <p>
        Your packaging team has a deadline at the end of next month: sign off the artwork for a
        consumer-pack refresh that&apos;ll ship into UK supermarkets, US mass-market and a couple of
        pharmacy chains. Marketing has tabled a QR code on the front of the pack — the brand has
        been promising one for two years — and finance has just realised that the major retailers in
        two of those channels announced their Sunrise-2027 cutoffs last quarter. Suddenly the
        question isn&apos;t whether to put a QR on the box; it&apos;s whether the QR you&apos;re
        putting on the box is the right kind, and whether the EAN-13 you were going to print next to
        it can stay.
      </p>
      <p>
        That conversation is happening on every brand-side packaging team right now. This guide is
        an attempt to make it short. <strong>Sunrise 2027</strong> is a global GS1 commitment to
        have retail point-of-sale scanners accept 2D barcodes alongside 1D EAN / UPC by the end of
        2027. What that means in practice — what changes at the till, what ships on the consumer
        pack, who carries the cost — is more concrete than the headline suggests, and most of the
        work is doable now.
      </p>

      <GuideArticleSection id="the-short-answer" title="The Short Answer">
        <p>
          Sunrise 2027 is the industry commitment that retail POS scanners will accept 2D barcodes
          by end of 2027. The recommended carrier is a QR Code containing a <Link to="/barcode-guides/gs1-digital-link">GS1 Digital Link</Link> URL — one symbol
          carries the GTIN for the till and a working web URL for the shopper&apos;s phone. The
          existing 1D EAN-13 / UPC-A doesn&apos;t go away; the safe consumer-pack default through
          2027 is to print both, side by side.
        </p>
        <Compare
          panes={[
            {
              label: "Today (1D-only pack)",
              caption: "EAN-13 / UPC-A only. POS reads the GTIN; the phone can't do much.",
              children: (
                <BarcodeFigure
                  format="ean13"
                  value="5012345678900"
                  formatLabel="EAN-13 (1D)"
                  caption={
                    <>
                      Existing consumer pack barcode encoding GTIN <code>5012345678900</code> — fast
                      to scan, opaque to the phone.
                    </>
                  }
                />
              ),
            },
            {
              label: "Sunrise-ready pack",
              caption: "Same EAN-13 plus a GS1 Digital Link QR — POS and phone both work.",
              children: (
                <BarcodeFigure
                  format="qrcode"
                  value="https://id.gs1.org/01/05012345678900/10/ABC123?17=261231"
                  formatLabel="QR · GS1 Digital Link"
                  caption={
                    <>
                      QR carrying a GS1 Digital Link URL — same GTIN, plus batch <code>ABC123</code> and expiry <code>2026-12-31</code>; opens as a web URL when scanned by a
                      phone, returns the GTIN at the till.
                    </>
                  }
                />
              ),
            },
          ]}
        />
      </GuideArticleSection>

      <GuideArticleSection id="before-after" title="Before vs After Sunrise 2027">
        <p>
          The most useful way to think about the change is a single table: what a typical
          consumer-pack scan looks like before and after the transition, and who has to do something
          to get from one to the other.
        </p>
        <TableScroll>
          <table>
            <caption className="sr-only">
              Before vs after Sunrise 2027 across scanner type, accepted symbologies, data carried,
              and who has to change.
            </caption>
            <thead>
              <tr>
                <th scope="col">
                  Aspect
                </th>
                <th scope="col">
                  Before Sunrise 2027
                </th>
                <th scope="col">
                  After Sunrise 2027
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">
                  Scanner type
                </th>
                <td>
                  Laser line or 2D imager — 1D required
                </td>
                <td>
                  2D imager required at the lane level
                </td>
              </tr>
              <tr>
                <th scope="row">
                  Accepted symbologies
                </th>
                <td>EAN-13, UPC-A, EAN-8</td>
                <td>
                  All of the above plus QR Code carrying a GS1 Digital Link and GS1 Data Matrix
                </td>
              </tr>
              <tr>
                <th scope="row">
                  Data carried
                </th>
                <td>GTIN only</td>
                <td>
                  GTIN plus optional batch (AI 10), expiry (AI 17), serial (AI 21) and a
                  shopper-facing URL
                </td>
              </tr>
              <tr>
                <th scope="row">
                  Who changes
                </th>
                <td>Status quo since the 1970s</td>
                <td>
                  Retailers upgrade scanners and POS software; brands add a 2D carrier to the
                  artwork
                </td>
              </tr>
              <tr>
                <th scope="row">
                  Shopper experience
                </th>
                <td>
                  Phone scan returns nothing useful from the barcode itself
                </td>
                <td>
                  Phone scan opens a product page, allergen list, recall notice or recipe
                </td>
              </tr>
            </tbody>
          </table>
        </TableScroll>
      </GuideArticleSection>

      <GuideArticleSection id="what-is-it" title="What Is GS1 Sunrise 2027?">
        <p>
          <strong>Sunrise 2027</strong> is a coordinated industry commitment, organised by GS1 and
          its member retailers and brand owners, to have point-of-sale systems worldwide accept 2D
          barcodes (alongside the existing 1D EAN / UPC) by the end of 2027. It isn&apos;t a law,
          and there&apos;s no fine attached to missing the date — but every major retail group in
          the GS1 membership has signed on, and most have published internal cutoffs of their own
          that land in 2026 or 2027.
        </p>
        <p>
          The carrier of choice is a QR Code that contains a <Link to="/barcode-guides/gs1-digital-link">GS1 Digital Link URL</Link> — a normal <code>https://</code> URL whose path follows a GS1-defined grammar, so the same symbol
          works as a web link when scanned by a phone and as a GS1 carrier when scanned by a
          Sunrise-ready POS. GS1 Data Matrix is the secondary option for industrial / pharma
          contexts where a phone scan isn&apos;t the point.
        </p>
        <Callout variant="spec" title="It's a commitment, not a regulation">
          <p>
            The exact wording in GS1&apos;s own materials is &quot;Ambition 2027&quot; — global POS
            acceptance of 2D codes by end of 2027. The mandate-shaped language brands hear from
            their retail partners comes from the individual retailer onboarding teams, not from GS1.
          </p>
        </Callout>
      </GuideArticleSection>

      <GuideArticleSection id="why-now" title="Why Now? The Data-Carrier Limits of 1D">
        <p>
          The 13 digits on an EAN-13 identify a product. They don&apos;t tell you which batch this
          unit came from, when it expires, what individual serial number it has, or how a shopper
          can scan it to see allergen information. None of that fits in a 1D barcode at any
          reasonable size — and the workarounds (separate batch labels printed alongside, FNC1 in a
          wider Code 128, sticker barcodes added at the depot) all push cost and error onto
          operations.
        </p>
        <p>
          A 2D code carries thousands of characters in the same physical footprint as a 1D barcode,
          with Reed–Solomon error correction that survives partial damage. That single change is
          enough to fold every separate label (batch, expiry, serial, URL, marketing QR) back into
          one symbol. Sunrise 2027 is the industry agreement to make that fold work at the till.
        </p>
        <p>
          For the deeper capacity / scanner / footprint comparison between the two families, see the <Link to="/barcode-guides/1d-vs-2d-barcodes">1D vs 2D barcodes guide</Link>.
        </p>
      </GuideArticleSection>

      <GuideArticleSection id="at-the-till" title="What Changes at the Till">
        <p>
          At the lane, almost nothing changes for the cashier or the shopper. The scanner beams
          whatever symbol is presented; the till looks up the GTIN; the price posts. The change is
          in what kinds of symbols the scanner is allowed to find.
        </p>
        <ul>
          <li>
            <strong>2D imager scanners</strong> read both 1D and 2D symbols — they&apos;re a strict
            superset of laser-line readers and have been the norm in self-scan and handheld units
            for a decade. After Sunrise, every lane needs an imager.
          </li>
          <li>
            <strong>POS software</strong> needs to extract the GTIN from a 2D payload as well as a
            1D one. For a GS1 Digital Link URL the GTIN sits at a known path segment (after <code>/01/</code>); for a GS1 Data Matrix it&apos;s in AI 01. Mature POS stacks already
            parse both; older systems need a firmware refresh.
          </li>
          <li>
            <strong>The lookup pipeline</strong> stays identical. The till sees the same GTIN it
            always did and posts the same price; the AIs (batch, expiry) flow through to whatever
            back-office system wants them — typically inventory, traceability or recall management.
          </li>
        </ul>
      </GuideArticleSection>

      <GuideArticleSection id="for-brands" title="What Brands Need to Do">
        <p>
          The brand-side work is mostly artwork and master data. None of it is hard individually;
          the trick is doing it on the right cycle.
        </p>
        <ul>
          <li>
            <strong>Keep the 1D EAN / UPC.</strong> Print it on every consumer pack as before. The
            Sunrise transition is additive, not substitutional.
          </li>
          <li>
            <strong>Add a QR with a GS1 Digital Link URL.</strong> Place it near the EAN, or on the
            front of the pack if marketing wants to. The URL encodes the GTIN at the same path
            segment that POS expects, plus any AIs (batch, expiry, serial) that apply per-unit.
          </li>
          <li>
            <strong>Register the GTIN with GS1.</strong> Major retailers (Amazon, Walmart) now
            verify that GTINs trace back to a GS1-issued company prefix; resellers&apos; codes
            increasingly fail. See <Link to="/barcode-guides/what-is-a-gtin">What is a GTIN?</Link> for the basics.
          </li>
          <li>
            <strong>Decide on a resolver.</strong> The default is <code>https://id.gs1.org/01/&lt;GTIN&gt;…</code> which always works. If you want a
            branded URL (<code>https://yourbrand.com/p/01/&lt;GTIN&gt;…</code>) you need to stand up
            your own GS1 Digital Link resolver — usually a thin web service.
          </li>
          <li>
            <strong>Plan the artwork cycle.</strong> Don&apos;t do a special run just for Sunrise;
            pair the QR addition with your next packaging refresh.
          </li>
        </ul>
        <p>
          To prototype a Digital Link URL and render it as a QR for proofing, use the <Link to="/gs1-digital-link-generator">GS1 Digital Link Generator</Link>. To verify a
          payload that&apos;s already been printed, paste it into the decoder:
        </p>
        <TryTool
          to="/gs1-digital-link-decoder"
          label="GS1 Digital Link Decoder"
          description="Confirm a printed payload parses cleanly into structured GTIN + AI data."
          value="https://id.gs1.org/01/05012345678900/10/ABC123?17=261231"
          cta="Decode the worked example"
        />
      </GuideArticleSection>

      <GuideArticleSection id="for-retailers" title="What Retailers Need to Do">
        <p>
          The retailer-side work is mostly hardware and POS software, with a side order of
          master-data plumbing.
        </p>
        <ul>
          <li>
            <strong>Audit the scanner fleet.</strong> Anything laser-only — older self-scan kiosks,
            some older handheld units — needs replacing with a 2D imager. Imagers are
            backwards-compatible with 1D, so this is a one-way swap.
          </li>
          <li>
            <strong>Upgrade POS software</strong> to extract the GTIN from a GS1 Digital Link URL or
            a GS1 Data Matrix payload. The major POS vendors have been shipping this for a couple of
            years; smaller franchise systems often need a firmware push.
          </li>
          <li>
            <strong>Decide the AI capture policy.</strong> Some retailers want batch and expiry to
            flow into inventory; others ignore them and just take the GTIN. Either is fine, but the
            policy needs to be explicit so the integration partners know what to do.
          </li>
          <li>
            <strong>Communicate the cutoff to suppliers.</strong> Every major retailer that has
            committed to Sunrise has published a date by which suppliers should ship 2D on consumer
            packs. Brand-side teams plan around those, so retailers that haven&apos;t published a
            date are quietly slowing their suppliers down.
          </li>
        </ul>
      </GuideArticleSection>

      <GuideArticleSection id="dual-carrier" title="The Dual-Carrier Interim">
        <p>
          Through 2026, 2027 and most likely well into 2028, the safe consumer-pack default is to
          carry both barcodes on the same artwork: the 1D EAN-13 (or UPC-A) for the existing POS
          lanes, and a 2D QR with a GS1 Digital Link for the upgraded ones and for the
          shopper&apos;s phone. The same GTIN sits inside both.
        </p>
        <Callout variant="in-practice" title="Don't strip the 1D too early">
          <p>
            The single biggest mistake brands make at this stage is removing the 1D barcode the
            moment they print the QR. Until every retailer in your channel has confirmed their POS
            reads 2D, the 1D barcode is the only thing the older lanes can scan. Strip it too early
            and you&apos;ll get rejected at goods-in.
          </p>
        </Callout>
        <p>
          The pack-design conversation is mostly about placement. Practical defaults: keep the 1D
          EAN where it&apos;s always been (back of pack, near the base); put the QR somewhere a
          shopper would naturally look — front of pack for engagement, base of pack for
          traceability. The two symbols don&apos;t have to be adjacent.
        </p>
      </GuideArticleSection>

      <GuideArticleSection id="misconceptions" title="Common Misconceptions">
        <p>
          Most confusion about Sunrise 2027 comes from reading it as a 1D phase-out or as a legal
          mandate. Five beliefs come up again and again, and each one is wrong in a specific way.
        </p>
        <ul>
          <li>
            <strong>&quot;Sunrise 2027 means 1D goes away.&quot;</strong> No. The 1D barcode stays
            on the pack and keeps working at the till. Sunrise is about 2D acceptance, not 1D
            removal.
          </li>
          <li>
            <strong>&quot;A plain marketing QR is good enough.&quot;</strong> No. A QR that opens <code>https://yourbrand.com/campaign</code> isn&apos;t a GS1 Digital Link — POS
            can&apos;t extract the GTIN from it. The URL has to follow the GS1 path grammar.
          </li>
          <li>
            <strong>&quot;My retailer is mandating Sunrise.&quot;</strong> Almost always that&apos;s
            the retailer&apos;s internal cutoff dressed up as a GS1 mandate. GS1 itself doesn&apos;t
            mandate; individual retailers do, and their dates may differ from 2027.
          </li>
          <li>
            <strong>&quot;Every brand needs a custom resolver domain.&quot;</strong> No. The default <code>id.gs1.org</code> resolver works for any GTIN. A custom resolver is useful for
            branding and analytics, not for Sunrise compliance.
          </li>
          <li>
            <strong>&quot;Data Matrix on consumer packs is Sunrise-ready.&quot;</strong> It is,
            technically, but a shopper&apos;s phone won&apos;t open a Data Matrix as a URL. For
            consumer-facing packs the recommended carrier is a QR with a Digital Link; reserve Data
            Matrix for industrial / pharma cases.
          </li>
        </ul>
      </GuideArticleSection>

      <GuideArticleSection id="quick-decision" title="Quick Decision Guide">
        <p>
          If you only need the practical answer for the next pack you&apos;re signing off, the rules
          collapse to four short ones:
        </p>
        <ul>
          <li>
            <strong>Print the 1D EAN-13 / UPC-A</strong> as you always have. Don&apos;t strip it
            before your retail partners confirm their POS is 2D-ready.
          </li>
          <li>
            <strong>Add a QR Code carrying a GS1 Digital Link URL</strong> — same GTIN, plus any
            per-unit AIs (batch, expiry, serial) you have. Use <code>https://id.gs1.org/01/&lt;GTIN&gt;…</code> unless you have a reason to stand up a
            branded resolver.
          </li>
          <li>
            <strong>Validate the artwork before press</strong>. Render the QR with the <Link to="/gs1-digital-link-generator">GS1 Digital Link Generator</Link>, then decode it
            back to confirm POS will read the GTIN and the AIs.
          </li>
          <li>
            <strong>Time the change to your next artwork cycle</strong>. There&apos;s rarely a
            business case for a Sunrise-only print run; pair it with whatever pack refresh is
            already scheduled.
          </li>
        </ul>
      </GuideArticleSection>

      <GuideArticleSection id="requirements" title="GS1 Sunrise 2027 Requirements">
        <p>
          GS1 Sunrise 2027 asks retailers to have checkout scanners and POS software that read 2D
          barcodes by the end of 2027. It puts no legal duty on brands; what a brand prints comes
          from each retailer&apos;s supplier requirements.
        </p>
        <p>
          For a brand, a 2D symbol only counts toward the Sunrise 2027 initiative if its content
          follows GS1 syntax. That means either a GS1 Digital Link URI or a GS1 element string, the
          FNC1-flagged format that writes each value after its Application Identifier, such as (01)
          for the GTIN.
        </p>
        <p>A consumer pack that meets these requirements checks four boxes:</p>
        <ul>
          <li>
            The GTIN in the 2D code is registered to your GS1 Company Prefix and matches the GTIN in
            the 1D barcode on the same pack.
          </li>
          <li>
            In an element string, AI (01) always holds 14 digits, so a GTIN-13 gets one leading zero
            and a GTIN-12 gets two.
          </li>
          <li>
            Batch (AI 10), serial (AI 21) and expiry (AI 17) are optional; add them only when the
            print process can change the code for each batch or unit.
          </li>
          <li>
            The EAN-13 or UPC-A stays on the pack until each retailer confirms its lanes read 2D.
          </li>
        </ul>
        <h3>Sunrise 2027 2D Barcodes</h3>
        <p>
          GS1 accepts four combinations of symbol and data syntax as Sunrise 2027 2D barcodes at
          retail checkout. They use two symbologies and differ in how the data is written inside the
          symbol.
        </p>
        <TableScroll>
          <table>
            <caption className="sr-only">
              The four GS1 2D barcode options for retail point of sale, by symbology and data syntax.
            </caption>
            <thead>
              <tr>
                <th scope="col">2D barcode</th>
                <th scope="col">Symbology</th>
                <th scope="col">Data inside</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">QR Code with GS1 Digital Link URI</th>
                <td>QR Code (ISO/IEC 18004)</td>
                <td>An https web address with the GTIN after /01/</td>
              </tr>
              <tr>
                <th scope="row">Data Matrix with GS1 Digital Link URI</th>
                <td>Data Matrix (ISO/IEC 16022)</td>
                <td>The same kind of web address</td>
              </tr>
              <tr>
                <th scope="row">GS1 QR Code</th>
                <td>QR Code (ISO/IEC 18004)</td>
                <td>A GS1 element string that starts with FNC1</td>
              </tr>
              <tr>
                <th scope="row">GS1 DataMatrix</th>
                <td>Data Matrix (ISO/IEC 16022)</td>
                <td>A GS1 element string that starts with FNC1</td>
              </tr>
            </tbody>
          </table>
        </TableScroll>
        <p>
          GS1 DataMatrix with an element string is the usual pick where a product already carries
          one for regulatory traceability, such as prescription medicines.
        </p>
        <p>
          To compare the two Digital Link options on your own GTIN, the <Link to="/gs1-digital-link-generator">Sunrise 2027 QR code and Data Matrix generator</Link> draws the same URL in either symbol and exports it as PNG or SVG.
        </p>
      </GuideArticleSection>

      <GuideArticleSection id="qr-codes-digital-link" title="Sunrise 2027 QR Codes and GS1 Digital Link">
        <p>
          A Sunrise 2027 QR code is a standard QR Code whose content is a GS1 Digital Link URI. The
          checkout scanner reads the GTIN from the URI path, and a phone opens the same address as a
          web page.
        </p>
        <p>
          The URI is built in a fixed order. It starts with a resolver domain, either id.gs1.org or a
          domain the brand runs, followed by /01/ and the GTIN. Key qualifiers come next in the path,
          in the order /22/ for consumer product variant, /10/ for batch or lot and /21/ for serial
          number.
        </p>
        <p>
          Data attributes do not belong in the path. Expiry date (AI 17), best-before date (AI 15)
          and net weight go in the query string, so a complete example reads
          https://id.gs1.org/01/09506000134352/10/ABC123?17=261231.
        </p>
        <p>To make one and check it before artwork sign-off:</p>
        <ol>
          <li>
            Enter the full GTIN in the generator; it pads the GTIN to 14 digits and rejects a wrong
            check digit.
          </li>
          <li>Add batch, serial or expiry only if the print run can change them.</li>
          <li>
            Keep https://id.gs1.org as the resolver, or enter your brand domain if it runs a GS1
            Digital Link resolver.
          </li>
          <li>Render it as a QR Code and download the SVG for the artwork file.</li>
          <li>
            Paste the URL from the proof into the <Link to="/gs1-digital-link-decoder">GS1 Digital Link decoder</Link> and confirm that the GTIN and every AI come back as entered.
          </li>
        </ol>
        <p>
          On the artwork, keep a quiet zone of at least four modules on every side of the QR Code,
          as ISO/IEC 18004 requires. Text, graphics or a pack edge inside that margin is a common
          reason a printed symbol fails to scan.
        </p>
      </GuideArticleSection>

      <GuideKeyTakeaways
        items={[
          "Sunrise 2027 is a GS1 commitment, not a regulation — global retail POS accepts 2D barcodes by end of 2027.",
          "The recommended carrier is a QR Code containing a GS1 Digital Link URL, alongside the existing 1D EAN / UPC.",
          "The existing 1D barcode stays on the pack and keeps working; dual carriers are the safe consumer-pack default through 2027.",
          "Brand work is mostly artwork + master data; retailer work is mostly scanner fleet + POS software.",
          "Validate every GS1 Digital Link with the decoder before sending artwork to press — POS extracts the GTIN from a known path segment, and the wrong syntax breaks the scan.",
        ]}
      />

      <GuideArticleSection id="references" title="References">
        <p>The statements in this guide trace back to three GS1 sources.</p>
        <ul>
          <li>
            GS1 &quot;2D at POS&quot; programme — the canonical landing page for Sunrise 2027
            milestones and adopter guidance: <a
              href="https://www.gs1.org/standards/barcodes"
              rel="noopener noreferrer"
              target="_blank"
            >
              gs1.org/standards/barcodes
            </a>
            .
          </li>
          <li>
            GS1 Digital Link standard — the URL grammar that makes one 2D symbol serve both POS and
            phone: <a
              href="https://www.gs1.org/standards/barcodes"
              rel="noopener noreferrer"
              target="_blank"
            >
              gs1.org/standards/barcodes
            </a>
            .
          </li>
          <li>
            GS1 General Specifications — the spec document that defines Application Identifiers and
            the 1D / 2D symbology constraints. Published by <a
              href="https://www.gs1.org/standards/barcodes"
              rel="noopener noreferrer"
              target="_blank"
            >
              GS1
            </a> and updated annually.
          </li>
        </ul>
      </GuideArticleSection>

      <p>
        For the URL syntax behind the dual-carrier QR — the GS1 Digital Link grammar that makes one
        2D symbol serve both POS and phone — see the <Link to="/barcode-guides/gs1-digital-link">GS1 Digital Link guide</Link>.
      </p>

      <FaqSection heading="Frequently Asked Questions" items={FAQ} />
    </GuideLayout>
  );
}
